import React, { useEffect, useRef, useState } from 'react';

const ROWS_GAP = 0; // altura de cada linha é controlada via CSS (--row-h)

function SideImage({ src, side, index }) {
  const [failed, setFailed] = useState(false);
  return (
    <div
      className={`showcase-img showcase-img--${side}`}
      style={{ '--active-index': index }}
      aria-hidden="true"
    >
      {src && !failed && <img src={src} alt="" onError={() => setFailed(true)} draggable="false" />}
    </div>
  );
}

// Faixa deslizável (mobile): no toque usa a rolagem nativa do navegador (inércia e
// encaixe por CSS); com mouse, arrastar move a faixa.
function DragStrip({ children }) {
  const areaRef = useRef(null);

  useEffect(() => {
    const area = areaRef.current;
    let down = false;
    let startX = 0;
    let startScroll = 0;
    let moved = false;

    const onDown = (e) => {
      if (e.pointerType !== "mouse") return;
      down = true;
      moved = false;
      startX = e.clientX;
      startScroll = area.scrollLeft;
      area.classList.add("is-dragging");
    };
    const onMove = (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 3) moved = true;
      area.scrollLeft = startScroll - dx;
    };
    const onUp = () => {
      if (!down) return;
      down = false;
      area.classList.remove("is-dragging");
      if (moved) {
        const card = area.firstElementChild && area.firstElementChild.children[1];
        const step = card ? card.offsetLeft - area.firstElementChild.children[0].offsetLeft : area.clientWidth;
        area.scrollTo({ left: Math.round(area.scrollLeft / step) * step, behavior: "smooth" });
      }
    };

    area.addEventListener("pointerdown", onDown);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      area.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, []);

  return (
    <div className="showcase-drag" ref={areaRef}>
      <div className="showcase-strip">{children}</div>
    </div>
  );
}

// Mockup com janela de navegador (desktop) + celular sobrepostos. Quando há uma
// captura de página inteira, ela rola do topo até o fim dentro das duas telas.
function DeviceMockup({ desktopSrc, phoneSrc, scrolling }) {
  const cls = scrolling ? " is-scrolling" : "";
  return (
    <div className="device-mockup" aria-hidden="true">
      <div className="device-browser">
        <div className="device-bar"><i /><i /><i /></div>
        <div className={`device-screen${cls}`}>
          {desktopSrc && <img src={desktopSrc} alt="" draggable="false" />}
        </div>
      </div>
      <div className="device-phone">
        <div className="device-notch" />
        <div className={`device-screen${cls}`}>
          {phoneSrc && <img src={phoneSrc} alt="" draggable="false" />}
        </div>
      </div>
    </div>
  );
}

// Lista de títulos grandes; ao passar o mouse, destaca o item e mostra
// duas imagens laterais referentes a ele. No mobile, cada serviço mostra
// suas imagens numa faixa arrastável.
export default function Showcase({ items, className = '' }) {
  const [active, setActive] = useState(0);
  const current = items[active];

  return (
    <div className={`showcase ${className}`} style={{ '--count': items.length + ROWS_GAP }}>
      <SideImage key={`l-${active}`} side="left" src={current.imageLeft} index={active} />
      <SideImage key={`r-${active}`} side="right" src={current.imageRight} index={active} />
      <ul className="showcase-list" onMouseLeave={() => setActive(0)}>
        {items.map((item, i) => (
          <li key={item.title} className={`showcase-item${i === active ? ' is-active' : ''}`}>
            <button
              type="button"
              className="showcase-btn"
              onMouseEnter={() => setActive(i)}
              onFocus={() => setActive(i)}
              onClick={() => setActive(i)}
            >
              <span className="showcase-num">{String(i + 1).padStart(2, '0')}.</span>
              <span className="showcase-title">{item.title}</span>
              {item.tags && <span className="showcase-tags">{item.tags}</span>}
            </button>
          </li>
        ))}
      </ul>

      <div className="showcase-mobile">
        <DragStrip>
          {items.map((item) => (
            <article key={item.title} className="showcase-card">
              <h3 className="showcase-mobile-title">{item.title}</h3>
              {item.tags && <p className="showcase-mobile-tags">{item.tags}</p>}
              {item.scrollImage ? (
                <DeviceMockup desktopSrc={item.scrollImage} phoneSrc={item.scrollImage} scrolling />
              ) : (
                <div className="showcase-card-imgs">
                  {[item.imageLeft, item.imageRight].filter(Boolean).map((src) => (
                    <div className="showcase-slide" key={src}>
                      <img src={src} alt="" draggable="false" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    </div>
                  ))}
                </div>
              )}
            </article>
          ))}
        </DragStrip>
      </div>
    </div>
  );
}
